/**
 * Priority in the hotel's words, and who may choose High (2026-10-31).
 *
 * The rule for reading a stored value is `api/task-priority.ts`; this is
 * what the screens say about it.
 *
 * THE WORDS FOLLOW THE SITE. What each level does at the robots is
 * published by the worker with the task catalog (`priority_effects`,
 * `{ normal: 'planned', high: 'ahead' }` from worker 0.4.40, copied onto
 * every catalog entry by the api-server). Until a worker says so the
 * robots do not act on priority -- worker 0.4.39 never puts it into the
 * request -- and the form says exactly that. So one console release is
 * true before the worker ships, after it, and after a worker rollback.
 */
import { useContext } from 'react';
import { AuthContext } from '../../auth/AuthContext';
import { hasAnyRole } from '../../auth/roles';
import { TASK_HIGH_PRIORITY_ROLES } from '../../auth/types';
import { PRIORITY_HIGH, isHighPriority, type PriorityLevel } from '../../api/task-priority';
import type { TaskCatalogEntry } from '../../api/v1';

export const PRIORITY_LABEL: Readonly<Record<PriorityLevel, string>> = {
  0: 'Normal',
  1: 'High',
};

/** The line under the choice while the site does not say what the levels
 *  do: the worker predates priority, or was rolled back to one that does. */
export const PRIORITY_NOT_YET = "Saved with the job. The robots don't act on priority yet.";

/**
 * Each published effect in words. NOT "in turn" for Normal: RMF re-plans
 * every waiting job on each new booking to finish them all soonest, so a
 * short job can overtake a long one (design study, 2026-10-31).
 */
const EFFECT_WORDS: Readonly<Record<string, string>> = {
  planned:
    'The robot fits it in with the other jobs, in the order that gets them all done soonest.',
  ahead: 'Goes ahead of waiting jobs. It does not stop the job the robot is doing now.',
};

/** Why High cannot be chosen, said beside it. */
export const HIGH_PRIORITY_LOCKED = 'Only a supervisor can choose High.';

/** Said beside a High that someone who may not set it is editing: it stays
 *  High, and lowering it is one way. */
export const HIGH_PRIORITY_KEPT =
  'A supervisor chose High. Your other changes keep it. Save it as Normal and only a supervisor can set it back.';

/**
 * The site's published effects, from whichever catalog entry carries them
 * (the api-server copies the same map onto every entry); null when a
 * loaded catalog has none, or its map names neither level.
 *
 * UNDEFINED WHILE NOT KNOWN: the catalog is still loading, or could not
 * be read (review PRIO-R4, 2026-10-31). That is not "the site publishes
 * nothing", and saying the robots don't act on priority then would be a
 * false line flashed on every open at a site whose robots do.
 */
export function priorityEffects(
  catalog: readonly TaskCatalogEntry[] | null | undefined,
): Readonly<Record<string, string>> | null | undefined {
  if (!catalog) return undefined;
  for (const entry of catalog) {
    const fx = entry.priority_effects;
    if (fx && typeof fx === 'object' && ('normal' in fx || 'high' in fx)) return fx;
  }
  return null;
}

/** The one line under the choice for `level`: nothing while the effects
 *  are not known (`undefined`), the not-yet line when the site publishes
 *  none, the effect in words when it names one this console knows, and
 *  nothing for a word from a newer worker (the help note still explains
 *  the choice). */
export function priorityEffectText(
  effects: Readonly<Record<string, string>> | null | undefined,
  level: PriorityLevel,
): string | null {
  if (effects === undefined) return null;
  if (!effects) return PRIORITY_NOT_YET;
  const effect = effects[level === PRIORITY_HIGH ? 'high' : 'normal'];
  return typeof effect === 'string' ? (EFFECT_WORDS[effect] ?? null) : null;
}

/** Whether these roles may choose High (`TASK_HIGH_PRIORITY_ROLES`). */
export function canSetHighPriority(roles: Iterable<string> | null | undefined): boolean {
  return hasAnyRole(roles, TASK_HIGH_PRIORITY_ROLES);
}

/**
 * Whether the signed-in person may choose High, by the roles the console
 * is showing -- a "view as" front desk sees High locked, as that role
 * would. Read from the auth CONTEXT, not `useAuth()`, which throws where
 * no provider is mounted; no provider means no role, and High locked.
 */
export function useCanSetHighPriority(): boolean {
  return canSetHighPriority(useContext(AuthContext)?.roles);
}

/**
 * A favourite's Run confirmation (`what`: "Vacuum"). A High favourite goes
 * as High only when a supervisor runs it; anyone else's run goes as
 * NORMAL, and the api-server says so afterwards (`priority_lowered`).
 *
 * Decided 2026-10-31: said before the run, never refused and never
 * silently High. A favourite is a template anyone on the site may run, the
 * job still gets done, and Normal pushes nobody's work back; a refusal
 * would leave the front desk with an urgent job and no way to send it
 * (`task_priority.py`).
 */
export function favoriteRunMessage(what: string, priority: unknown, canSetHigh: boolean): string {
  const head = `Dispatches a ${what} task immediately`;
  if (!isHighPriority(priority)) return `${head}.`;
  return canSetHigh
    ? `${head}, at High priority.`
    : `${head}. It was saved as High, but only a supervisor can send High, so it goes as Normal.`;
}
